<template>
  <div class="highlights-select-modal">
    <!-- Header -->
    <div class="header highlights-select-modal__header">
      <div class="header__back-link-wrap">
        <LinkWithIcon class="header__back-link" @click="emit('cancel')" isDottedUnderline>
          <template #icon><IconEmbedded name="arrow-left_2-5" :size="16" /></template>
          <span>Back</span>
        </LinkWithIcon>
      </div>

      <h2 class="global-h2 header__title">{{ displayedAlbumTitle }}</h2>
      <div v-if="props.subTitle" class="header__subtitle">
        {{ props.subTitle }}
      </div>
    </div>
    <!-- / Header -->

    <!-- Album switcher -->
    <div v-if="!pickingFromContentSummaryId" class="album-switcher highlights-select-modal__album-switcher">
      <LinkWithIcon isDottedUnderline @click="isPickContentModalVisible = true">
        <template #icon><IconEmbedded name="folder_2" :size="19" /></template>
        <span>Select from another album</span>
      </LinkWithIcon>
      <span class="album-switcher__separator"> </span>
      <LinkWithIcon isDottedUnderline @click="isUploadSectionOpen = true">
        <template #icon><IconEmbedded name="upload_2" :size="17" /></template>
        <span>Upload photos</span>
      </LinkWithIcon>
    </div>

    <div v-else class="album-switcher highlights-select-modal__album-switcher">
      <LinkWithIcon isDottedUnderline @click="clearExternalAlbum()">
        <template #icon><IconEmbedded name="arrow-left_2-5" :size="16" /></template>
        <span>Show original album</span>
      </LinkWithIcon>
    </div>
    <!-- / Album switcher -->

    <!-- Upload section -->
    <PhotoUploadSection
      v-if="isUploadSectionOpen"
      class="highlights-select-modal__upload-section"
      inputId="social-post-upload"
      v-model="uploadedAssetIds"
      :selectedAssetIds="_selectedAssetIds"
      @select="assetId => selectOrDeselectFile(true, assetId)"
      @deselect="assetId => selectOrDeselectFile(false, assetId)"
      @close="isUploadSectionOpen = false"
    />
    <!-- / Upload section -->

    <ul v-if="isLoadingExternalAlbum" class="highlights-select-modal__thumbnails-list highlights-select-modal__thumbnails-list--skeleton">
      <li v-for="i in 12" :key="i" class="highlights-select-modal__thumbnail highlights-select-modal__thumbnail--skeleton"></li>
    </ul>

    <ul v-else class="highlights-select-modal__thumbnails-list">
      <SrpFileThumbnail
        class="highlights-select-modal__thumbnail"
        v-for="assetId in displayedAssetIds"
        :key="assetId"
        :fileName="assetId"
        :fancyboxCaption="_selectedAssetIds.includes(assetId) ? 'Image is selected' : null"
        galleryPostfix="postPhotoSelectionModal"
        :isDeleteButton="false"
        @delete="() => {}"
        isEntireAreaCheckable
      >
        <input
          type="checkbox"
          :value="assetId"
          :checked="_selectedAssetIds.includes(assetId)"
          :disabled="isAtLimit && !_selectedAssetIds.includes(assetId)"
          @change="$event => selectOrDeselectFile(($event.target as HTMLInputElement).checked, assetId)"
        />
      </SrpFileThumbnail>
    </ul>

    <div class="highlights-select-modal__sticky-panel">
      <PhotoLimitWarning :isAtLimit="isAtLimit" />

      <SelectedFilesPanel
        v-if="_selectedAssetIds.length"
        class="highlights-select-modal__selected-files-panel"
        :selectedFiles="_selectedAssetIds.map(id => ({ serverId: id })) as any"
        @remove="assetId => selectOrDeselectFile(false, assetId)"
        @reorder="newArray => (_selectedAssetIds = newArray.map(({ serverId }) => serverId))"
        :isPreviewLinkVisible="false"
      />
    </div>

    <PickFromExistingContentModal
      v-if="isPickContentModalVisible"
      v-model:isVisible="isPickContentModalVisible"
      :customerId="props.customerId"
      :calendarItem="null"
      @close="isPickContentModalVisible = false"
      @picked="handleContentSummarySelected"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";

// Components
import SrpFileThumbnail from "@components/ui/SrpFileThumbnail.vue";
import SelectedFilesPanel from "@components/SelectedFilesPanel.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import PickFromExistingContentModal from "@views/Community/ContentCalendar/steps/modals/PickFromExistingContentModal.vue";
import PhotoUploadSection from "./PhotoUploadSection.vue";
import PhotoLimitWarning from "./PhotoLimitWarning.vue";

// Repos
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";

// Types
import { SocialShareSummaryPayload } from "@contracts/shareStop";
import { ContentSummaryPreview } from "@contracts/contentCalendar";

// Constants
import { MAX_PHOTOS_PER_POST } from "@/constants/social";

const props = withDefaults(
  defineProps<{
    customerId: string;
    title?: string;
    subTitle?: string;
    allAssetIds: Array<string>;
    // fallbackAssetIds: Array<string>;
    selectedAssetIds: Array<string>;
    saveCallBack?: ((selectedAssetIds: Array<string>) => Promise<void>) | null;
  }>(),
  {
    customerId: "",
    title: "Select Photos",
    subTitle: "",
    allAssetIds: () => [],
    // fallbackAssetIds: () => [],
    selectedAssetIds: () => [],
    saveCallBack: null,
  }
);

const emit = defineEmits<{
  (e: "save", selectedAssetIds: Array<string>): void;
  (e: "cancel"): void;
}>();

// External album state =======================================================
const isPickContentModalVisible = ref<boolean>(false);
const pickingFromContentSummaryId = ref<string | null>(null);
const externalAlbumPayload = ref<SocialShareSummaryPayload | null>(null);
const isLoadingExternalAlbum = ref<boolean>(false);

// Upload section state =======================================================
const isUploadSectionOpen = ref<boolean>(false);
const uploadedAssetIds = ref<Array<string>>([]);

// Selected assets not in content album =======================================
const selectedAssetsNotInContentAlbum = ref<Array<string>>([]);

// Select file ================================================================
function selectOrDeselectFile(isSelected: boolean, assetId: string) {
  if (isSelected) {
    _selectedAssetIds.value.push(assetId);
  } else {
    _selectedAssetIds.value = _selectedAssetIds.value.filter(_assetId => _assetId !== assetId);
  }
}

// selectedAssetIds local copy ================================================
const _selectedAssetIds = ref<Array<string>>([]);

onMounted(() => {
  _selectedAssetIds.value = props.selectedAssetIds;

  // Calculate selected assets that are not in the content album
  // This allows the user to deselect them without them completely disappearing.
  selectedAssetsNotInContentAlbum.value = props.selectedAssetIds.filter(assetId => !props.allAssetIds.includes(assetId));
});

// Displayed asset IDs ========================================================
const displayedAssetIds = computed(() => {
  if (pickingFromContentSummaryId.value && externalAlbumPayload.value) {
    return externalAlbumPayload.value.assets;
  }
  // Append selected assets that are not in the content album
  return [...props.allAssetIds, ...selectedAssetsNotInContentAlbum.value];
});

const displayedAlbumTitle = computed(() => {
  if (pickingFromContentSummaryId.value && externalAlbumPayload.value) {
    return `Select from: "${externalAlbumPayload.value.title}"`;
  }
  return props.title;
});

// Selection limit ============================================================
const isAtLimit = computed(() => _selectedAssetIds.value.length >= MAX_PHOTOS_PER_POST);

// Save =======================================================================
const isSaving = ref<boolean>(false);

async function save() {
  if (props.saveCallBack) {
    isSaving.value = true;
    await props.saveCallBack(_selectedAssetIds.value);
    isSaving.value = false;
  }
  emit("save", _selectedAssetIds.value);
}

// Handle content summary selection ===========================================
async function handleContentSummarySelected(contentSummary: ContentSummaryPreview) {
  isPickContentModalVisible.value = false;
  pickingFromContentSummaryId.value = contentSummary.id;

  isLoadingExternalAlbum.value = true;
  externalAlbumPayload.value = await ShareStopSummaryRepo.loadSocialShareSummaryPayloadFromSummaryId(props.customerId, contentSummary.id);
  isLoadingExternalAlbum.value = false;
}

function clearExternalAlbum() {
  pickingFromContentSummaryId.value = null;
  externalAlbumPayload.value = null;
}

// Define expose ==============================================================
defineExpose({
  save,
  cancel: () => emit("cancel"),
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/mixins/animations/loading-bg.scss";
@import "./photo-upload";

// Header =====================================================================
.header {
  &__back-link-wrap {
    padding-bottom: 15px;
    margin-bottom: 20px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__back-link {
  }

  &__title {
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__subtitle {
  }
}

// Section header =============================================================
.section-header {
  display: flex;
  align-items: center;

  &__icon {
    margin-right: 2px;
  }

  &__title {
  }

  &__gray-text {
    color: rgba(0, 0, 0, 0.5);
    font-weight: 500;
  }
}

// AI suggested alternatives ==================================================
.ai-suggested-alternatives {
  padding: 23px 0 18px;
  position: relative;
  z-index: 0;

  &::before {
    content: "";
    width: calc(100% + 60px);
    height: 100%;
    position: absolute;
    inset: 0 auto auto -30px;
    z-index: -1;
    background: rgba(229, 243, 242, 1);
  }

  &__section-header {
    margin-bottom: 13px;
  }

  &__thumbnails-list {
    width: calc(100% + 15px);
    padding: 0;
    margin: 0 0 40px;
    display: flex;
    flex-wrap: wrap;
    list-style: none;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// Album switcher =============================================================
.album-switcher {
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;

  &__separator {
    color: rgba(0, 0, 0, 0.5);
    font-size: 13px;
  }

  &__title {
    margin-top: 8px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 13px;
  }
}

// Highlights select modal ====================================================
.highlights-select-modal {
  color: #5b5b5b;
  font: 14px/19px sans-serif;

  &__header {
    margin-bottom: 15px;
  }

  &__album-switcher {
    margin-bottom: 15px;
  }

  &__ai-suggested-alternatives {
    margin-bottom: 30px;
  }

  &__thumbnails-list {
    width: calc(100% + 15px);
    padding: 0;
    margin: 0 0 40px;
    display: flex;
    flex-wrap: wrap;
    list-style: none;

    &--skeleton {
      height: 200px;
      align-content: flex-start;
      position: relative;
      overflow: hidden;
      mask-image: linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%);
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__thumbnail {
    width: 113px;
    margin: 0 11px 11px 0;

    &--skeleton {
      @include loadingBgAnimation;
      height: 85px;
    }
  }

  &__sticky-panel {
    background: white;
    position: sticky;
    bottom: -40px;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .highlights-select-modal {
    &__thumbnail {
      width: calc(100% / 3 - 12px);
    }

    &__sticky-panel {
      width: calc(100% + 40px);
      margin-left: -20px;
      bottom: -37px;
    }

    &__selected-files-panel {
      &::after {
        border-radius: 0;
      }
    }
  }
}
</style>
